import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Popover } from 'react-tiny-popover';
import { Text, Flex, ActionIcon, Menu, Tooltip, Box, Textarea, Button } from '@mantine/core';
import { TbWand, TbDotsVertical, TbTrash } from 'react-icons/tb';
import { FaICursor } from 'react-icons/fa6';
import { BiEraser } from 'react-icons/bi';
import { ErrorText } from '../../ErrorText';
import ConfirmModal from '../../../../../../components/ConfirmModal';
import Draggable from 'react-draggable';
import { useHotkeys } from 'react-hotkeys-hook';
import useOutsideClick from '../../../../../../hooks/useOutsideClick';

const FieldMenu = ({
  item,
  onItemClick,
  tableName,
  getFieldTypeIcon,
  selectedItem,
  getPromptValue,
  onCreatePrompt,
  onUpdatePrompt,
  onDeletePrompt,
  isLockedPeriod,
  onClearColumn,
  isManual,
  classes,
  error,
  isLoading,
  showDelete,
  onDeleteField,
  draggableProps,
  dragHandleProps,
  innerRef,
  isDeleteLoading,
  onOpenPrompt
}) => {
  const promptBox = useRef(null);
  const textareaRef = useRef(null);

  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [text, setText] = useState('');
  const [popoverOffset, setPopoverOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [initialText, setInitialText] = useState('');
  const dragNodeRef = useRef(null);
  const table = item?.table ?? tableName;

  const prompt = useMemo(() => {
    return getPromptValue?.(item?.name, item?.table);
  }, [getPromptValue, item?.name, item?.table]);

  const hasUnsavedChanges = () => {
    const currentText = (text || '').trim();
    return currentText !== '' && currentText !== initialText;
  };

  const requestClosePrompt = React.useCallback(() => {
    if (!isPromptOpen) return;

    if (hasUnsavedChanges()) {
      setShowConfirmModal(true);
    } else {
      closePrompt();
    }
  }, [isPromptOpen, text, initialText]);

  useOutsideClick(promptBox, requestClosePrompt);

  useEffect(() => {
    if (!isPromptOpen) return;
    const id = requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch (_) {
        /* ignore: some inputs don't support selection */
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isPromptOpen]);

  const openPrompt = e => {
    e?.stopPropagation?.();
    const promptText = prompt?.prompt_text || '';
    setText(promptText);
    setInitialText(promptText);
    setPopoverOffset({ x: 0, y: 0 });
    setIsPromptOpen(true);
  };

  const closePrompt = () => {
    setIsPromptOpen(false);
    setText('');
    setInitialText('');
  };

  const submitPrompt = e => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const value = (text || '').trim();
    if (!value) return;

    const onDone = () => closePrompt();

    if (prompt) {
      onUpdatePrompt(prompt.id, value, item.name, onDone);
    } else {
      onCreatePrompt(value, item.name, table, onDone);
    }
  };

  const handleActionIconClick = e => {
    e.stopPropagation();
    if (isPromptOpen) requestClosePrompt();
  };

  const deletePrompt = e => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!prompt) return;

    onDeletePrompt(prompt.id, () => {
      closePrompt();
    });
  };

  const handleConfirmDiscard = () => {
    setShowConfirmModal(false);
    closePrompt();
  };

  const handleCancelDiscard = () => {
    setShowConfirmModal(false);
  };

  const tooltipText = useMemo(() => {
    return (item?.description || '').trim();
  }, [item?.description]);

  useHotkeys(
    'escape',
    e => {
      e.preventDefault();
      e.stopPropagation();
      requestClosePrompt();
    },
    {
      enableOnFormTags: ['TEXTAREA', 'INPUT', 'SELECT']
    },
    [requestClosePrompt]
  );

  const handleDragStart = useCallback(
    e => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setIsDragging(true);
    },
    [setIsDragging]
  );

  const handleDrag = useCallback(
    (e, data) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setPopoverOffset({ x: data.x, y: data.y });
    },
    [setPopoverOffset]
  );

  const handleDragStop = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const handleOpenPromptClick = e => {
    e.stopPropagation();
    onOpenPrompt?.(item);
    openPrompt(e);
  };

  const handleDeleteFieldClick = e => {
    e.stopPropagation();
    onDeleteField();
  };

  const handleClearColumnClick = e => {
    e.stopPropagation();
    onClearColumn(item);
  };

  const tooltip = (
    <Tooltip
      disabled={!tooltipText}
      label={
        <Text fz="12px" lh="17px" style={{ whiteSpace: 'pre-line' }}>
          {tooltipText}
        </Text>
      }
      maw="400px"
      multiline
    >
      <Flex
        ref={innerRef}
        {...draggableProps}
        {...dragHandleProps}
        onClick={onItemClick}
        onDragStart={() => setIsPromptOpen(false)}
        onMouseDownCapture={() => {
          if (isPromptOpen) requestClosePrompt();
        }}
        onPointerDownCapture={() => {
          if (isPromptOpen) requestClosePrompt();
        }}
        p="3.5px 8px"
        fz="12px"
        className={classes.categoryName}
        align="center"
        gap="5px"
        bg={item.isEmpty ? 'white' : 'blue.0'}
        c={selectedItem ? 'blue.6' : item.isEmpty ? 'black' : 'blue.6'}
        style={{
          border: `1px solid ${selectedItem ? '#228BE6' : item.isEmpty ? '#CED4DA' : '#E7F5FF'}`
        }}
      >
        {getFieldTypeIcon(item)}

        {error?.id === item.id && (
          <Popover
            padding={5}
            isOpen={true}
            content={<ErrorText text={error?.text} type={error?.type} />}
            positions={['bottom', 'top']}
          >
            <div />
          </Popover>
        )}

        {item.name}

        {!!prompt && <TbWand fontSize="12px" style={{ minWidth: '12px' }} />}

        {!isLockedPeriod && (
          <Menu.Target>
            <ActionIcon
              id={`grid-table-field-menu-toggle-${item.id}`}
              onClick={handleActionIconClick}
              size="xs"
              variant="subtle"
              color="rgba(0, 0, 0, 1)"
              aria-label="Settings"
              c={selectedItem ? '#228BE6' : item.isEmpty ? '#868E96' : '#228BE6'}
            >
              <TbDotsVertical />
            </ActionIcon>
          </Menu.Target>
        )}
      </Flex>
    </Tooltip>
  );

  return (
    <>
      <Box id={`grid-table-field-menu-shell-${item.id}`}>
        <Popover
          isOpen={isPromptOpen}
          positions={['bottom', 'top']}
          align="center"
          padding={1}
          reposition={true}
          usePortal={true}
          containerStyle={{ zIndex: '1000' }}
          content={
            <Draggable
              nodeRef={dragNodeRef}
              position={popoverOffset}
              disabled={showConfirmModal}
              cancel={'textarea,button,input,select,option,[role="button"],a'}
              onStart={handleDragStart}
              onDrag={handleDrag}
              onStop={handleDragStop}
            >
              <div ref={dragNodeRef}>
                <Box
                  id={`grid-table-field-menu-prompt-${item.id}`}
                  ref={promptBox}
                  component="form"
                  onSubmit={submitPrompt}
                  onClick={e => e.stopPropagation()}
                  sx={() => ({
                    width: 351,
                    background: 'white',
                    border: `1px solid ${isDragging ? '#228BE6' : '#CED4DA'}`,
                    borderRadius: 4,
                    boxShadow: '0px 1px 3px 0px #0000000D',
                    padding: 8,
                    cursor: 'pointer',
                    pointerEvents: showConfirmModal ? 'none' : 'auto'
                  })}
                >
                  <Textarea
                    id={`grid-table-field-menu-prompt-input-${item.id}`}
                    ref={textareaRef}
                    onChange={e => setText(e.target.value)}
                    value={text}
                    placeholder="Enter your prompt"
                    aria-label="Prompt text"
                    w="100%"
                    resize="none"
                    autosize
                    minRows={3}
                    maxRows={8}
                    disabled={showConfirmModal}
                  />

                  <Flex
                    id={`grid-table-field-menu-prompt-actions-${item.id}`}
                    justify="flex-end"
                    gap="16px"
                    mt="16px"
                  >
                    {!!prompt && (
                      <Button
                        id={`grid-table-field-menu-delete-prompt-${item.id}`}
                        onClick={deletePrompt}
                        size="xs"
                        color="red"
                        disabled={showConfirmModal}
                        type="button"
                        loading={isDeleteLoading}
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      id={`grid-table-field-menu-submit-prompt-${item.id}`}
                      rightSection={<TbWand />}
                      type="submit"
                      loading={isLoading}
                      size="xs"
                      disabled={showConfirmModal}
                    >
                      Submit
                    </Button>
                  </Flex>
                </Box>
              </div>
            </Draggable>
          }
        >
          {tooltip}
        </Popover>

        <Menu.Dropdown id={`grid-table-field-menu-dropdown-${item.id}`} w="170px" p="16px">
          {!isManual && (
            <Menu.Item
              id={`grid-table-field-menu-open-prompt-${item.id}`}
              leftSection={<FaICursor fontSize="16px" color="#ADB5BD" />}
              onClick={handleOpenPromptClick}
            >
              Open Prompt
            </Menu.Item>
          )}

          <Menu.Item
            id={`grid-table-field-menu-clear-${item.id}`}
            onClick={handleClearColumnClick}
            leftSection={<BiEraser fontSize="16px" color="#ADB5BD" />}
          >
            Clear Values
          </Menu.Item>

          {!!showDelete && typeof onDeleteField === 'function' && (
            <Menu.Item
              id={`grid-table-field-menu-delete-${item.id}`}
              onClick={handleDeleteFieldClick}
              leftSection={<TbTrash fontSize="16px" color="#ADB5BD" />}
            >
              Delete
            </Menu.Item>
          )}
        </Menu.Dropdown>

        <ConfirmModal
          id={`grid-table-field-menu-confirm-${item.id}`}
          isOpen={showConfirmModal}
          title="Discard changes?"
          description="You have unsaved changes in your prompt. Are you sure you want to close without saving? Your changes will be lost."
          onClose={handleCancelDiscard}
          onConfirm={handleConfirmDiscard}
          colorSchema="red"
          confirmButtonTitle="Yes, Discard"
        />
      </Box>
    </>
  );
};

export default FieldMenu;
